import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllInventoryItems, getAllCategories } from "../../../redux/actions/InventoryAction";
import { API_URI } from "../../../constants/GlobalConstants";
import AddCustomMaterialModal from "../ui/AddCustomMaterialModal";
import Loader from "../../layout/Loader";
import axios from "axios";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";

const MaterialOrdersTable = ({
  materialOrders = [],
  handleAddMaterialOrder,
  handleRemoveMaterialOrder,
  showAddMaterialModal,
  setShowAddMaterialModal,
  dealer,
  setMaterialOrders,
}) => {
  const dispatch = useDispatch();
  const inventoryItems = useSelector((state) => state.getInvItemsByCat);
  const { data: inventoryData = [], loading: inventoryLoading } = inventoryItems;
  const { categories, loading: categoriesLoading, error: categoriesError } = useSelector(
    (state) => state.getCategories
  );
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  const [currentCategories, setCurrentCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dealerName, dealerId] = dealer.includes("_")
    ? dealer.split("_")
    : [dealer, dealer];
  const [pricingData, setPricingData] = useState([]);
  const [showPriceNotification, setShowPriceNotification] = useState(false);
  const [priceEditLink, setPriceEditLink] = useState("");

  useEffect(() => {
    if (userInfo?.token) {
      // Fetch inventory items and categories
      if (!inventoryData.length || inventoryLoading) {
        dispatch(getAllInventoryItems(userInfo.token));
      }
      dispatch(getAllCategories(userInfo.token));
    }
  }, [dispatch, userInfo?.token, inventoryData.length, inventoryLoading]);

  useEffect(() => {
    const categories = materialOrders.map((order) => order.category || "");
    setCurrentCategories(categories);
  }, [materialOrders]);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const response = await axios.get(
          `${API_URI}/api/dealer/materials/prices/${dealerId}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        setPricingData(response.data);
      } catch (error) {
        console.error("Failed to fetch pricing data:", error);
      }
    };

    if (dealerId && userInfo?.token) {
      fetchPricingData();
    }
  }, [dealerId, userInfo?.token]);

  const handleCategoryChange = (index, categoryValue) => {
    handleAddMaterialOrder(index, "category", categoryValue);
    const updatedCategories = [...currentCategories];
    updatedCategories[index] = categoryValue;
    setCurrentCategories(updatedCategories);
  };

const handleMaterialChange = (index, value) => {
    if (value === "custom") {
      setSelectedCategory(currentCategories[index]);
      setShowAddMaterialModal(true);
    } else {
      const updatedOrders = [...materialOrders];
      updatedOrders[index] = {
        ...updatedOrders[index],
        material: value,
      };

      const pricingItem = pricingData.find(
        (item) => item.inventoryItem === value
      );

      if (pricingData.length === 0 || !pricingItem || 
        (pricingItem && pricingItem.pricingType === "fixed" && pricingItem.price === 0)){
        setPriceEditLink(`/dealer/edit-material-rate/${dealerId}`);
        setShowPriceNotification(true);
        updatedOrders[index] = {
          ...updatedOrders[index],
          customCost: 0,
          sellingPrice: 0,
          discountRate: 0,
        };
      } else {
        const inventoryItem = inventoryData.find((item) => item._id === value);
        const sellingPrice = inventoryItem?.sellingPrice || 0;

        const unitPrice =
          pricingItem.pricingType === "discount"
            ? sellingPrice * (1 - pricingItem.price / 100)
            : pricingItem.price;

        // Use the current quantity or default to 1 if not set
        const quantity = updatedOrders[index].quantity || 1;
        updatedOrders[index] = {
          ...updatedOrders[index],
          customCost: Number((unitPrice * quantity).toFixed(2)),
          sellingPrice: Number(sellingPrice.toFixed(2)),
          discountRate:
            pricingItem.pricingType === "discount"
              ? Number(pricingItem.price.toFixed(2))
              : 0,
        };
      }

      setMaterialOrders(updatedOrders);
    }
  };

  const handleQuantityChange = (index, value) => {
    const updatedOrders = [...materialOrders];
    const quantity = value ? parseFloat(value) : 0;

    updatedOrders[index] = {
      ...updatedOrders[index],
      quantity,
    };

    // If a material is selected and pricing data exists, update customCost
    if (updatedOrders[index].material) {
      const pricingItem = pricingData.find(
        (item) => item.inventoryItem === updatedOrders[index].material
      );

      if (pricingItem && pricingItem.price !== 0) {
        const inventoryItem = inventoryData.find(
          (item) => item._id === updatedOrders[index].material
        );
        const sellingPrice = inventoryItem?.sellingPrice || 0;

        const unitPrice =
          pricingItem.pricingType === "discount"
            ? sellingPrice * (1 - pricingItem.price / 100)
            : pricingItem.price;

        updatedOrders[index] = {
          ...updatedOrders[index],
          customCost: Number((unitPrice * quantity).toFixed(2)),
          sellingPrice: Number(sellingPrice.toFixed(2)),
          discountRate:
            pricingItem.pricingType === "discount"
              ? Number(pricingItem.price.toFixed(2))
              : 0,
        };
      } else {
        // If no pricing data, keep fields as is or set to 0
        updatedOrders[index] = {
          ...updatedOrders[index],
          customCost: 0,
          sellingPrice: 0,
          discountRate: 0,
        };
      }
    }

    setMaterialOrders(updatedOrders);
  };

  const handleCustomCostChange = (index, value) => {
    const updatedOrders = [...materialOrders];
    updatedOrders[index] = {
      ...updatedOrders[index],
      customCost: value ? parseFloat(value) : 0,
    };
    setMaterialOrders(updatedOrders);
  };

  const handleNewMaterialAdded = () => {
    if (userInfo?.token) {
      dispatch(getAllInventoryItems(userInfo.token));
    }
  };

  // Determine the pricing description based on pricingData
  const pricingDescription =
    pricingData.length > 0
      ? pricingData[0].pricingType === "discount"
        ? "The discount will be reduced from the inventory selling price."
        : "The fixed price set in the dealer material pricing is used as material pricing."
      : "No pricing data available. Please set pricing for materials.";

  if (inventoryLoading || categoriesLoading) {
    return <Loader />;
  }

  if (categoriesError) {
    return <div className="text-center text-danger">{categoriesError}</div>;
  }

  return (
    <div className="container-fluid bg-dark text-white">
      {/* Dynamic Pricing Description */}
      <div className="mb-3">
        <p className="text-white">{pricingDescription}</p>
      </div>

      <div className="table-card-container">
        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table
            className="table table-bordered table-sm"
            style={{ backgroundColor: "white", border: "1px solid black" }}
          >
            <thead>
              <tr>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Category
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Material
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Quantity
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Selling Price
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Discount Rate (%)
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Custom Cost
                </th>
                <th className="text-black" style={{ border: "1px solid black" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {materialOrders.map((order, index) => {
                // Map the category value (ID or name) to the display name
                const categoryDisplay = categories?.find(
                  (cat) => cat.value === order.category
                )?.name || order.category || "";

                return (
                  <tr key={index}>
                    <td style={{ border: "1px solid black" }}>
                      <select
                        className="form-select bg-secondary text-white border-light"
                        value={order.category || ""}
                        onChange={(e) =>
                          handleCategoryChange(index, e.target.value)
                        }
                      >
                        <option value="">Select Category</option>
                        {categories?.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <select
                        className="form-select bg-secondary text-white border-light"
                        value={order.material || ""}
                        onChange={(e) =>
                          handleMaterialChange(index, e.target.value)
                        }
                        disabled={!order.category}
                      >
                        <option value="">Select Material</option>
                        <option value="custom">Create Material</option>
                        {order.category &&
                          inventoryData
                            .filter((item) => {
                              // Match inventory items by category (ID or name)
                              const inventoryCategory = item.category;
                              return inventoryCategory === order.category;
                            })
                            .map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.name}
                              </option>
                            ))}
                      </select>
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <input
                        type="number"
                        className="form-control bg-secondary text-white border-light"
                        value={order.quantity || ""}
                        onChange={(e) =>
                          handleQuantityChange(index, e.target.value)
                        }
                        min="0"
                      />
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <input
                        type="number"
                        className="form-control bg-secondary text-white border-light"
                        value={order.sellingPrice || 0}
                        readOnly
                      />
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <input
                        type="number"
                        className="form-control bg-secondary text-white border-light"
                        value={order.discountRate || 0}
                        readOnly
                      />
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <input
                        type="number"
                        className="form-control bg-secondary text-white border-light"
                        value={order.customCost || ""}
                        onChange={(e) =>
                          handleCustomCostChange(index, e.target.value)
                        }
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td style={{ border: "1px solid black" }}>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleRemoveMaterialOrder(index)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button
            className="btn btn-primary mt-3"
            onClick={() => handleAddMaterialOrder(null, null, null)}
          >
            Add New Material Order
          </button>
        </div>
      </div>

      <AddCustomMaterialModal
        show={showAddMaterialModal}
        handleClose={() => {
          setShowAddMaterialModal(false);
          setSelectedCategory("");
        }}
        category={selectedCategory}
        onMaterialAdded={handleNewMaterialAdded}
      />

      <Dialog
        open={showPriceNotification}
        onClose={() => setShowPriceNotification(false)}
      >
        <DialogTitle>Price Not Set</DialogTitle>
        <DialogContent>
          <p>
            The price for the selected material is not set. Please set the price
            first.
          </p>
          <p>
            <Link to={priceEditLink} rel="noopener noreferrer">
              Click here to set the price
            </Link>
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPriceNotification(false)}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaterialOrdersTable;